import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { selectUser } from "../../../store/auth/selectors";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
  Spinner,
} from "reactstrap";
import toastr from "toastr";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import ConfirmModal from "../../../components/Common/ConfirmModal";
import Spinners from "../../../components/Common/Spinner";
import {
  useClientGoCardlessIbpBookingPayment,
  useClientStripeBookingPayment,
  useGetClientBookingDetails,
} from "../../../api/booking";
import {
  useAttachPaymentMethods,
  useDetachPaymentMethod,
  usePaymentMethodsQuery,
  useSetPrimaryPaymentMethod,
} from "../../../api/user-payment";
import { mediaUrl } from "../../../helpers/mediaHelper";
import { hasUnpaidInvoices } from "../clientBookingAccess";

const PAYMENT_STRIPE = "stripe";
const PAYMENT_GOCARDLESS = "gocardLessIBP";
const PAYMENT_TYPE_PARTIAL = "partial_payment";
const PAYMENT_TYPE_FULL = "full_payment";

/** True if the client has completed at least one booking (prefers explicit API flags when present). */
function clientHasCompletedPriorBooking(user) {
  const c = user?.client;
  if (!c) return false;
  if (typeof c.noOfBookings === "number") return c.noOfBookings > 0;
  return false;
}

/** Supports `{ status, data: { paymentMethods, defaultPaymentMethodId } }` and legacy array shapes */
const parsePaymentMethodsPayload = (payload) => {
  if (!payload) return { methods: [], defaultPaymentMethodId: null };
  if (Array.isArray(payload)) {
    return { methods: payload, defaultPaymentMethodId: null };
  }
  const data = payload.data;
  if (!data) return { methods: [], defaultPaymentMethodId: null };
  if (Array.isArray(data.paymentMethods)) {
    return {
      methods: data.paymentMethods,
      defaultPaymentMethodId: data.defaultPaymentMethodId ?? null,
    };
  }
  if (Array.isArray(data)) {
    return { methods: data, defaultPaymentMethodId: null };
  }
  if (Array.isArray(data.data)) {
    return { methods: data.data, defaultPaymentMethodId: null };
  }
  return { methods: [], defaultPaymentMethodId: null };
};

const ClientBookingPaymentMethod = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const {
    data: bookingRes,
    isLoading: bookingLoading,
    error: bookingError,
  } = useGetClientBookingDetails(slug);

  const booking = useMemo(
    () => bookingRes?.data?.data ?? bookingRes?.data ?? bookingRes,
    [bookingRes]
  );

  const paymentAccessDenied =
    !bookingLoading && !bookingError && booking && !hasUnpaidInvoices(booking);

  useEffect(() => {
    if (!paymentAccessDenied || !slug) return;
    navigate(`/client/bookings/${slug}`, { replace: true });
  }, [paymentAccessDenied, slug, navigate]);

  const authUser = useSelector(selectUser);
  const hasCompletedPriorBooking = clientHasCompletedPriorBooking(authUser);
  const hasPriorBooking = hasCompletedPriorBooking;
  const initialMethod =
    booking?.paymentType === PAYMENT_GOCARDLESS && hasPriorBooking
      ? PAYMENT_GOCARDLESS
      : PAYMENT_STRIPE;
  const [method, setMethod] = useState(null);
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPE_PARTIAL);
  const [detachModalPm, setDetachModalPm] = useState(null);

  useEffect(() => {
    setMethod(null);
    setPaymentType(PAYMENT_TYPE_PARTIAL);
    setDetachModalPm(null);
  }, [slug]);

  const resolvedMethod =
    method ??
    (initialMethod === PAYMENT_GOCARDLESS
      ? PAYMENT_GOCARDLESS
      : PAYMENT_STRIPE);

  const allInvoices = useMemo(
    () => (Array.isArray(booking?.invoiceInfo) ? booking.invoiceInfo : []),
    [booking]
  );
  /** Invoices that can still be paid (exclude refunds and already-paid rows). */
  const payableInvoices = useMemo(
    () =>
      allInvoices.filter(
        (inv) => inv?.invoiceKind !== "refund" && inv?.isPaid !== true
      ),
    [allInvoices]
  );
  const upfrontInvoices = useMemo(
    () =>
      payableInvoices.filter((invoice) => invoice?.invoiceKind === "upfront"),
    [payableInvoices]
  );
  const hasSingleInvoice = payableInvoices.length === 1;
  const singleInvoice = hasSingleInvoice ? payableInvoices[0] : null;
  const singleInvoiceIsUpfront = singleInvoice?.invoiceKind === "upfront";
  const hasUpfrontInvoices = upfrontInvoices.length > 0;
  const hasAnyInvoices = payableInvoices.length > 0;

  useEffect(() => {
    if (hasSingleInvoice) {
      const nextPaymentType = singleInvoiceIsUpfront
        ? PAYMENT_TYPE_PARTIAL
        : PAYMENT_TYPE_FULL;
      if (paymentType !== nextPaymentType) {
        setPaymentType(nextPaymentType);
      }
      return;
    }
    if (paymentType === PAYMENT_TYPE_PARTIAL && !hasUpfrontInvoices) {
      setPaymentType(PAYMENT_TYPE_FULL);
    }
  }, [paymentType, hasUpfrontInvoices, hasSingleInvoice, singleInvoiceIsUpfront]);

  const selectedInvoices =
    paymentType === PAYMENT_TYPE_PARTIAL ? upfrontInvoices : payableInvoices;
  const selectedInvoiceIds = selectedInvoices
    .map((invoice) => invoice?.invoiceId)
    .filter(Boolean);
  const canSubmitPayment = selectedInvoiceIds.length > 0;

  const {
    data: pmPayload,
    isLoading: pmLoading,
    isFetching: pmFetching,
    refetch: refetchPaymentMethods,
  } = usePaymentMethodsQuery({ enabled: resolvedMethod === PAYMENT_STRIPE });

  const { methods: paymentMethods, defaultPaymentMethodId } = useMemo(
    () => parsePaymentMethodsPayload(pmPayload),
    [pmPayload]
  );

  const attachMutation = useAttachPaymentMethods();
  const stripePaymentMutation = useClientStripeBookingPayment();
  const goCardlessIbpPaymentMutation = useClientGoCardlessIbpBookingPayment();
  const detachMutation = useDetachPaymentMethod();
  const primaryMutation = useSetPrimaryPaymentMethod();

  const closeDetachModal = () => {
    if (detachMutation.isPending) return;
    setDetachModalPm(null);
  };

  const confirmDetachCard = () => {
    if (!detachModalPm?.id) return;
    detachMutation.mutate(detachModalPm.id, {
      onSuccess: () => {
        toastr.success("Card detached.");
        setDetachModalPm(null);
        refetchPaymentMethods();
      },
    });
  };

  const busy =
    attachMutation.isPending ||
    stripePaymentMutation.isPending ||
    goCardlessIbpPaymentMutation.isPending ||
    detachMutation.isPending ||
    primaryMutation.isPending;

  const handleAttachCard = () => {
    attachMutation.mutate(
      { bookingSlug: slug },
      {
        onSuccess: (res) => {
          const url = res?.data?.url ?? res?.data?.data?.url ?? res?.url;
          if (url) {
            window.location.href = url;
          } else {
            toastr.error("No checkout URL returned. Please try again.");
          }
        },
      }
    );
  };

  const handleSubmitStripe = () => {
    if (!slug) return;
    if (!canSubmitPayment) {
      toastr.warning("No invoices available for this payment selection.");
      return;
    }
    if (paymentMethods.length === 0) {
      toastr.warning("Attach a card first, then complete setup.");
      return;
    }
    stripePaymentMutation.mutate(
      { bookingSlug: slug, paymentType, invoiceIds: selectedInvoiceIds },
      {
        onSuccess: () => {
          toastr.success("Payment submitted.");
          navigate(`/client/bookings/${slug}/payment-success`);
        },
      }
    );
  };

  const handleSubmitGoCardlessIbp = () => {
    if (!slug) return;
    if (!canSubmitPayment) {
      toastr.warning("No invoices available for this payment selection.");
      return;
    }
    goCardlessIbpPaymentMutation.mutate(
      { bookingSlug: slug, paymentType, invoiceIds: selectedInvoiceIds },
      {
        onSuccess: (res) => {
          const authorisationUrl =
            res?.data?.data?.goCardLess?.billingRequestFlow?.billing_request_flows
              ?.authorisation_url ??
            res?.data?.goCardLess?.billingRequestFlow?.billing_request_flows
              ?.authorisation_url ??
            res?.goCardLess?.billingRequestFlow?.billing_request_flows
              ?.authorisation_url;

          if (authorisationUrl) {
            window.location.href = authorisationUrl;
            return;
          }

          toastr.error("No GoCardless authorisation URL returned. Please try again.");
        },
      }
    );
  };

  document.title = "Payments | Client";

  if (bookingLoading && !booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Payments" />
          <Spinners setLoading={() => {}} />
        </div>
      </div>
    );
  }

  if (bookingError || !booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Payments" />
          <Alert color="danger">Could not load this booking.</Alert>
          <Button tag={Link} to="/client/bookings" color="primary">
            Back to bookings
          </Button>
        </div>
      </div>
    );
  }

  if (paymentAccessDenied) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Payments" />
          <Spinners setLoading={() => {}} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Bookings" breadcrumbItem="Payments" />

        <Row className="justify-content-center">
          <Col lg={8}>
            <Card>
              <CardBody>
                <p className="text-muted mb-1">
                  Booking{" "}
                  <Link to={`/client/bookings/${slug}`} className="fw-semibold">
                    {slug}
                  </Link>
                </p>

                <h5 className="mb-3">
                  {payableInvoices.length === 0
                    ? "Nothing to pay"
                    : "Choose how you pay"}
                </h5>

                {payableInvoices.length === 0 ? (
                  <Button tag={Link} to="/client/bookings" color="primary">
                    Back to bookings
                  </Button>
                ) : (
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (resolvedMethod === PAYMENT_STRIPE)
                        handleSubmitStripe();
                      else handleSubmitGoCardlessIbp();
                    }}
                  >
                    {!hasSingleInvoice ? (
                      <FormGroup className="mb-3">
                        <Label className="form-label fw-semibold mb-2">
                          Payment option
                        </Label>
                        <div className="d-flex flex-column gap-2">
                          <div className="form-check">
                            <Input
                              type="radio"
                              id="payment-type-partial"
                              name="paymentType"
                              checked={paymentType === PAYMENT_TYPE_PARTIAL}
                              onChange={() =>
                                setPaymentType(PAYMENT_TYPE_PARTIAL)
                              }
                              disabled={busy || !hasUpfrontInvoices}
                            />
                            <Label
                              for="payment-type-partial"
                              className="form-check-label ms-2"
                            >
                              Partial payment (upfront invoice only)
                            </Label>
                          </div>
                          <div className="form-check">
                            <Input
                              type="radio"
                              id="payment-type-full"
                              name="paymentType"
                              checked={paymentType === PAYMENT_TYPE_FULL}
                              onChange={() => setPaymentType(PAYMENT_TYPE_FULL)}
                              disabled={busy || !hasAnyInvoices}
                            />
                            <Label
                              for="payment-type-full"
                              className="form-check-label ms-2"
                            >
                              Full payment (all invoices)
                            </Label>
                          </div>
                          {!hasUpfrontInvoices && hasAnyInvoices && (
                            <small className="text-muted">
                              Upfront invoices are not available for this
                              booking, so only full payment is available.
                            </small>
                          )}
                        </div>
                      </FormGroup>
                    ) : null}

                    <FormGroup className="mb-3">
                      <Label className="form-label fw-semibold mb-2">
                        Invoices
                      </Label>
                      {selectedInvoices.length === 0 ? (
                        <Alert color="warning" className="py-2 mb-0">
                          No invoices found for the selected payment option.
                        </Alert>
                      ) : (
                        <ListGroup>
                          {selectedInvoices.map((invoice) => (
                            <ListGroupItem
                              key={invoice.invoiceId}
                              className="d-flex flex-column gap-2"
                            >
                              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                <div className="d-flex flex-column">
                                  <span className="fw-semibold text-capitalize">
                                    {invoice.invoiceKind} invoice
                                  </span>
                                  <small className="text-muted">
                                    Amount: {invoice.invoiceAmount} | Due:{" "}
                                    {invoice.InvoiceDueDate}
                                  </small>
                                </div>
                                {invoice.invoiceURL ? (
                                  <Button
                                    tag="a"
                                    href={mediaUrl(invoice.invoiceURL)}
                                    target="_blank"
                                    rel="noreferrer"
                                    color="link"
                                    className="p-0"
                                  >
                                    Open in new tab
                                  </Button>
                                ) : null}
                              </div>
                              {invoice.invoiceURL ? (
                                <iframe
                                  title={`${invoice.invoiceKind}-invoice-${invoice.invoiceId}`}
                                  src={mediaUrl(invoice.invoiceURL)}
                                  className="w-100 rounded border"
                                  style={{ minHeight: "480px" }}
                                />
                              ) : null}
                            </ListGroupItem>
                          ))}
                        </ListGroup>
                      )}
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <div className="d-flex flex-column gap-2">
                        <div className="form-check">
                          <Input
                            type="radio"
                            id="pay-stripe"
                            name="paymentMethod"
                            checked={resolvedMethod === PAYMENT_STRIPE}
                            onChange={() => setMethod(PAYMENT_STRIPE)}
                            disabled={busy}
                          />
                          <Label
                            for="pay-stripe"
                            className="form-check-label ms-2"
                          >
                            Stripe (card)
                          </Label>
                        </div>
                        {hasPriorBooking && (
                          <div className="form-check">
                            <Input
                              type="radio"
                              id="pay-gocardless-ibp"
                              name="paymentMethod"
                              checked={resolvedMethod === PAYMENT_GOCARDLESS}
                              onChange={() => setMethod(PAYMENT_GOCARDLESS)}
                              disabled={busy}
                            />
                            <Label
                              for="pay-gocardless-ibp"
                              className="form-check-label ms-2"
                            >
                              GoCardless (instant bank payment)
                            </Label>
                          </div>
                        )}
                      </div>
                      {!hasPriorBooking && (
                        <p className="small text-muted mt-2 mb-0">
                          GoCardless IBP is available after at least one prior
                          completed booking.
                        </p>
                      )}
                    </FormGroup>

                    {resolvedMethod === PAYMENT_GOCARDLESS && (
                      <Alert color="info" className="py-2">
                        We will use GoCardless (instant bank payment) for this
                        booking. Submit to confirm.
                      </Alert>
                    )}

                    {resolvedMethod === PAYMENT_STRIPE && (
                      <>
                        <Alert color="light" className="border py-3">
                          <p className="mb-2">
                            Add a card via our secure Stripe checkout, then
                            return here and submit to link this booking to card
                            payments.
                          </p>
                          <Button
                            type="button"
                            color="primary"
                            outline
                            className="me-2 d-inline-flex align-items-center"
                            onClick={handleAttachCard}
                            disabled={busy}
                          >
                            {attachMutation.isPending ? (
                              <>
                                <Spinner
                                  size="sm"
                                  className="me-2 flex-shrink-0"
                                />{" "}
                                Opening checkout…
                              </>
                            ) : (
                              "Attach card"
                            )}
                          </Button>
                          <Button
                            type="button"
                            color="link"
                            className="p-0 align-baseline d-inline-flex align-items-center"
                            onClick={() => refetchPaymentMethods()}
                            disabled={pmLoading || pmFetching || busy}
                          >
                            {pmFetching ? (
                              <>
                                <Spinner
                                  size="sm"
                                  className="me-1 flex-shrink-0"
                                />
                                {pmLoading ? "Loading cards…" : "Refreshing…"}
                              </>
                            ) : (
                              "Refresh saved cards"
                            )}
                          </Button>
                        </Alert>

                        <h6 className="mt-3 mb-2">Saved cards</h6>
                        <p className="text-muted small mb-2">
                          Your default card is used for charges. Use{" "}
                          <strong>Make primary</strong> to change it, or{" "}
                          <strong>Detach card</strong> to remove a non-default
                          card.
                        </p>
                        {pmLoading || pmFetching ? (
                          <div className="text-muted small d-flex align-items-center">
                            <Spinner size="sm" className="me-2 flex-shrink-0" />
                            Loading payment methods…
                          </div>
                        ) : paymentMethods.length === 0 ? (
                          <p className="text-muted small mb-2">
                            No cards on file yet. Use “Attach card” first.
                          </p>
                        ) : (
                          <ListGroup className="mb-3">
                            {paymentMethods.map((pm) => {
                              const isDefault =
                                defaultPaymentMethodId != null &&
                                pm.id === defaultPaymentMethodId;
                              const isMakingPrimary =
                                primaryMutation.isPending &&
                                primaryMutation.variables === pm.id;
                              return (
                                <ListGroupItem
                                  key={pm.id}
                                  className="d-flex flex-wrap justify-content-between align-items-center gap-2"
                                >
                                  <span className="d-flex align-items-center gap-2 flex-wrap">
                                    {pm.card?.brand
                                      ? String(pm.card.brand).toUpperCase()
                                      : "Card"}{" "}
                                    ·••• {pm.card?.last4 ?? "----"}
                                    {isDefault && (
                                      <Badge
                                        color="success"
                                        pill
                                        className="text-uppercase"
                                      >
                                        Default
                                      </Badge>
                                    )}
                                  </span>
                                  <span className="d-flex gap-1 flex-shrink-0">
                                    {!isDefault && (
                                      <>
                                        <Button
                                          size="sm"
                                          color="primary"
                                          type="button"
                                          className="fw-semibold px-3 d-inline-flex align-items-center"
                                          disabled={busy}
                                          onClick={() =>
                                            primaryMutation.mutate(pm.id, {
                                              onSuccess: () => {
                                                toastr.success(
                                                  "Primary card updated."
                                                );
                                                refetchPaymentMethods();
                                              },
                                            })
                                          }
                                        >
                                          {isMakingPrimary ? (
                                            <>
                                              <Spinner
                                                size="sm"
                                                className="me-1 flex-shrink-0"
                                              />
                                              Updating…
                                            </>
                                          ) : (
                                            "Make primary"
                                          )}
                                        </Button>
                                        <Button
                                          size="sm"
                                          color="danger"
                                          type="button"
                                          className="fw-semibold px-3"
                                          disabled={busy}
                                          onClick={() => setDetachModalPm(pm)}
                                        >
                                          Detach card
                                        </Button>
                                      </>
                                    )}
                                  </span>
                                </ListGroupItem>
                              );
                            })}
                          </ListGroup>
                        )}
                      </>
                    )}

                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <Button
                        color="primary"
                        type="submit"
                        disabled={busy || !canSubmitPayment}
                        className="d-inline-flex align-items-center"
                      >
                        {stripePaymentMutation.isPending ||
                        goCardlessIbpPaymentMutation.isPending ? (
                          <>
                            <Spinner size="sm" className="me-2 flex-shrink-0" />{" "}
                            {stripePaymentMutation.isPending
                              ? "Processing payment…"
                              : "Redirecting to payment…"}
                          </>
                        ) : (
                          "Pay now"
                        )}
                      </Button>
                      <Button
                        tag={Link}
                        to="/client/bookings"
                        color="secondary"
                        outline
                        type="button"
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <ConfirmModal
          isOpen={Boolean(detachModalPm)}
          toggle={closeDetachModal}
          title="Detach this card?"
          message={
            detachModalPm ? (
              <>
                This removes{" "}
                <span className="fw-semibold text-body">
                  {detachModalPm.card?.brand
                    ? String(detachModalPm.card.brand).toUpperCase()
                    : "Card"}{" "}
                  ·••• {detachModalPm.card?.last4 ?? "----"}
                </span>{" "}
                from your saved payment methods. You can add it again later
                through Stripe checkout.
              </>
            ) : null
          }
          confirmLabel="Yes, detach card"
          cancelLabel="Cancel"
          confirmColor="danger"
          onConfirm={confirmDetachCard}
          isLoading={detachMutation.isPending}
        />
      </div>
    </div>
  );
};

export default ClientBookingPaymentMethod;
